import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Wallet, PackageCheck, TrendingUp, Brain, Sparkles, RefreshCw, Lock } from "lucide-react";
import Layout from "@/components/Layout";
import PlanUsageCard from "@/components/PlanUsageCard";

const REVENUE_RANGES = [
  { id: "7d",  label: "Ultimi 7 giorni" },
  { id: "30d", label: "Ultimi 30 giorni" },
  { id: "all", label: "Totale" },
];

function formatEuro(v) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(Number(v) || 0);
}

function RevenueCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#C5A059] mb-2">
        <Icon className="w-4 h-4" />
        <p className="text-[11px] uppercase tracking-[0.18em] font-medium">{label}</p>
      </div>
      <p className="font-serif-display text-3xl text-[#1A1A1A]">{value}</p>
    </div>
  );
}

const COACH_STATE_TONE = {
  ottimo:     { bg: "bg-emerald-50",  border: "border-emerald-200", text: "text-emerald-700",  label: "Ottimo" },
  buono:      { bg: "bg-sky-50",      border: "border-sky-200",     text: "text-sky-700",      label: "Buono" },
  attenzione: { bg: "bg-amber-50",    border: "border-amber-200",   text: "text-amber-700",    label: "Attenzione" },
  critico:    { bg: "bg-rose-50",     border: "border-rose-200",    text: "text-rose-700",     label: "Critico" },
};

function formatLastUpdated(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function Dashboard() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);

  const [revenueRange, setRevenueRange] = useState("30d");
  const [revenue, setRevenue] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  // AI Performance Coach™
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachForcing, setCoachForcing] = useState(false);

  const fetchSubscription = async () => {
    try {
      const sRes = await api.get("/me/subscription");
      const sData = sRes.data;
      setSubscription(sData);
      const key = `trico_80warn_${sData.plan}_${sData.period_end || "lifetime"}`;
      const limit = sData.plan === "free" ? sData.limit_total : sData.limit_period;
      const used = sData.plan === "free" ? sData.used_total : sData.used_period;
      if (limit && used / limit >= 0.8 && used < limit && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        toast.warning(`Hai usato ${used} su ${limit} analisi. Valuta l'upgrade.`, {
          action: { label: "Vedi piani", onClick: () => { window.location.href = "/piani"; } },
        });
      }
    } catch {
      // silenzioso: la PlanUsageCard gestisce lo stato subscription=null
    }
  };

  useEffect(() => { fetchSubscription(); }, []);

  useEffect(() => {
    let cancelled = false;
    setRevenueLoading(true);
    api.get(`/me/revenue?range=${revenueRange}`)
      .then(res => { if (!cancelled) setRevenue(res.data); })
      .catch(() => { if (!cancelled) setRevenue(null); })
      .finally(() => { if (!cancelled) setRevenueLoading(false); });
    return () => { cancelled = true; };
  }, [revenueRange]);

  // Auto-load coaching report al mount (cache 24h lato backend → free se valida)
  // PRO-only: per Free/Basic NON facciamo fetch e mostriamo direttamente la card lock.
  const isPro = (user?.plan || "free").toLowerCase() === "pro";

  const fetchCoaching = async (force = false) => {
    if (!isPro) return;  // hard guard: never call backend on non-pro
    if (force) setCoachForcing(true);
    else setCoachLoading(true);
    try {
      const res = await api.post(`/me/coaching${force ? "?force=true" : ""}`);
      setCoach(res.data);
    } catch {
      // silenzioso: lasciamo il widget in stato "non disponibile"
      setCoach(null);
    } finally {
      setCoachLoading(false);
      setCoachForcing(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isPro) fetchCoaching(false); }, [isPro]);

  return (
    <Layout>
      {/* Hero */}
      <div className="mb-10 animate-fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Dashboard</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] mb-3">
          Buongiorno{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
        </h1>
        <p className="text-[#737373] max-w-xl">
          Panoramica del tuo studio: risultati economici e coaching AI sul tuo metodo di lavoro.
        </p>
      </div>

  

      {/* Risultati economici — dati REALI solo da prodotti venduti */}
      <div className="mt-10 mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold">
            Risultati economici
          </span>
          {revenueLoading && (
            <span className="text-[10px] text-[#A3A3A3]">aggiornamento…</span>
          )}
        </div>
        <div className="inline-flex rounded-full border border-[#E5E5E0] bg-white p-1" data-testid="revenue-range">
          {REVENUE_RANGES.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRevenueRange(r.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                revenueRange === r.id
                  ? "bg-[#1A1A1A] text-white"
                  : "text-[#737373] hover:text-[#1A1A1A]"
              }`}
              data-testid={`revenue-range-${r.id}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10"
        data-testid="revenue-cards"
      >
        <RevenueCard
          icon={Wallet}
          label="Guadagno generato"
          value={formatEuro(revenue?.total_revenue_eur)}
          testid="revenue-total"
        />
        <RevenueCard
          icon={PackageCheck}
          label="Prodotti venduti"
          value={revenue?.products_sold ?? 0}
          testid="revenue-products"
        />
        <RevenueCard
          icon={TrendingUp}
          label="Media per cliente"
          value={formatEuro(revenue?.avg_per_client_eur)}
          testid="revenue-avg-client"
        />
        <RevenueCard
          icon={Brain}
          label="Guadagno per analisi"
          value={formatEuro(revenue?.avg_per_analysis_eur)}
          testid="revenue-avg-analysis"
        />
      </div>

      {/* AI Performance Coach™ */}
      <div
        className="rounded-2xl border border-[#E5E5E0] bg-white p-6 mb-10"
        data-testid="ai-performance-coach"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[#C5A059]" />
              <p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#C5A059]">
                AI Performance Coach™
              </p>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1A1A1A] text-white tracking-[0.14em]">
                PRO
              </span>
            </div>
            <p className="text-sm text-[#737373]">
              Consigli sul metodo di lavoro basati sui tuoi dati degli ultimi 30 giorni.
            </p>
          </div>
          {isPro && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => fetchCoaching(false)}
                disabled={coachLoading || coachForcing}
                className="text-xs px-3 py-1.5 rounded-full border border-[#E5E5E0] text-[#1A1A1A] hover:border-[#C5A059] disabled:opacity-50 transition-colors flex items-center gap-1.5"
                data-testid="coach-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${coachLoading ? "animate-spin" : ""}`} />
                Aggiorna analisi
              </button>
              <button
                type="button"
                onClick={() => fetchCoaching(true)}
                disabled={coachLoading || coachForcing}
                className="text-xs px-3 py-1.5 rounded-full bg-[#1A1A1A] hover:bg-[#C5A059] text-white disabled:opacity-50 transition-colors"
                data-testid="coach-force-btn"
                title="Bypassa la cache e rigenera ora"
              >
                {coachForcing ? "Rigenero…" : "Rigenera (force)"}
              </button>
            </div>
          )}
        </div>

        {/* Stato LOCK per Free/Basic — teaser di conversione */}
        {!isPro && (
          <div
            className="rounded-xl border border-[#E5E5E0] bg-gradient-to-br from-[#FAFAF7] to-white p-5 sm:p-6"
            data-testid="coach-lock"
          >
            <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-stretch">
              {/* Lock icon */}
              <div className="shrink-0 flex sm:flex-col items-center sm:items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-[#1A1A1A] text-[#C5A059] flex items-center justify-center">
                  <Lock className="w-5 h-5" />
                </div>
                <span className="hidden sm:inline-flex text-[10px] tracking-[0.18em] uppercase text-[#737373]">
                  Solo PRO
                </span>
              </div>

              {/* Teaser preview */}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#C5A059] mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  Esempio analisi AI
                </p>
                <p
                  className="text-[15px] leading-relaxed text-[#1A1A1A] italic mb-4 pl-3 border-l-2 border-[#C5A059]"
                  data-testid="coach-lock-preview"
                >
                  "Stai utilizzando il sistema in modo corretto, ma perdi
                  opportunità nel trasformare le analisi in percorsi completi
                  per il cliente."
                </p>

                <ul className="space-y-1.5 mb-5" data-testid="coach-lock-bullets">
                  <li className="flex gap-2 text-sm text-[#1A1A1A]">
                    <span className="text-[#C5A059] shrink-0">✓</span>
                    <span>Individua dove perdi clienti</span>
                  </li>
                  <li className="flex gap-2 text-sm text-[#1A1A1A]">
                    <span className="text-[#C5A059] shrink-0">✓</span>
                    <span>Ti suggerisce come migliorare il tuo metodo</span>
                  </li>
                </ul>

                <div className="flex flex-col items-start gap-2">
                  <Link
                    to="/piani"
                    className="inline-flex items-center px-5 py-2.5 rounded-full bg-[#1A1A1A] hover:bg-[#C5A059] text-white text-sm font-medium transition-colors"
                    data-testid="coach-upgrade-cta"
                  >
                    Sblocca la tua analisi AI
                  </Link>
                  <span className="text-[11px] text-[#737373]" data-testid="coach-lock-urgency">
                    Analisi aggiornata ogni 15 giorni
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stato vuoto / loading iniziale (solo PRO) */}
        {isPro && !coach && (coachLoading || coachForcing) && (
          <p className="text-sm text-[#737373] py-6 text-center" data-testid="coach-loading">
            Sto analizzando il tuo metodo di lavoro…
          </p>
        )}
        {isPro && !coach && !coachLoading && !coachForcing && (
          <p className="text-sm text-[#737373] py-6 text-center" data-testid="coach-empty">
            Nessun report ancora disponibile. Premi "Aggiorna analisi" per generarne uno.
          </p>
        )}

        {isPro && coach && (() => {
          const r = coach.report || {};
          const tone = COACH_STATE_TONE[r.stato_generale] || COACH_STATE_TONE.attenzione;
          return (
            <div className="space-y-5" data-testid="coach-report">
              {/* Header con stato + punteggio */}
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${tone.bg} ${tone.border} ${tone.text}`}
                  data-testid="coach-stato-badge"
                >
                  {tone.label}
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="font-serif-display text-3xl text-[#1A1A1A]" data-testid="coach-punteggio">
                    {r.punteggio ?? 0}
                  </span>
                  <span className="text-xs text-[#A3A3A3]">/ 100</span>
                </div>
                {coach.generated_at && (
                  <span className="text-[11px] text-[#A3A3A3] ml-auto" data-testid="coach-generated-at">
                    Aggiornato il {formatLastUpdated(coach.generated_at)}
                    {coach.from_cache ? " · (cache)" : ""}
                  </span>
                )}
              </div>

              {/* Sintesi */}
              {r.sintesi && (
                <p className="text-[15px] leading-relaxed text-[#1A1A1A]" data-testid="coach-sintesi">
                  {r.sintesi}
                </p>
              )}

              {/* Osservazioni + Consigli */}
              <div className="grid md:grid-cols-2 gap-5">
                <div data-testid="coach-osservazioni">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#737373] mb-3">
                    Osservazioni
                  </p>
                  <ul className="space-y-2">
                    {(r.osservazioni || []).map((o, i) => (
                      <li key={i} className="flex gap-2 text-sm text-[#1A1A1A]">
                        <span className="text-[#C5A059] font-medium shrink-0">{i + 1}.</span>
                        <span>{o}</span>
                      </li>
                    ))}
                    {(!r.osservazioni || r.osservazioni.length === 0) && (
                      <li className="text-sm text-[#A3A3A3]">Nessuna osservazione disponibile.</li>
                    )}
                  </ul>
                </div>
                <div data-testid="coach-consigli">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#737373] mb-3">
                    Consigli pratici
                  </p>
                  <ul className="space-y-2">
                    {(r.consigli || []).map((c, i) => (
                      <li key={i} className="flex gap-2 text-sm text-[#1A1A1A]">
                        <span className="text-[#C5A059] font-medium shrink-0">{i + 1}.</span>
                        <span>{c}</span>
                      </li>
                    ))}
                    {(!r.consigli || r.consigli.length === 0) && (
                      <li className="text-sm text-[#A3A3A3]">Nessun consiglio disponibile.</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
          {/* Plan + usage */}
      <PlanUsageCard subscription={subscription} />
    </Layout>
  );
}
